import type { WorkflowDateFormat } from '@/api/workflows';

/**
 * Semantic-validation rules the FE checks on blur for ShortText fields with a
 * `validate` property set. Each entry pairs the rule name (matches the BE
 * allow-list in WorkflowField.cs → IsValidValidateRule) with a runtime check
 * that returns a translated error message key when the value is wrong, or
 * null when it's fine.
 *
 * Empty / whitespace values short-circuit to null — "required" is enforced
 * separately by the existing required-flag UX. A blank optional SIN, for
 * example, should not surface a "must be 9 digits" error.
 */

export type ValidateRule = 'sin' | 'email' | 'phone' | 'postal' | 'regex';

export type ValidationResult = {
  errorKey: string;
  /**
   * Interpolation values for the message, passed straight to `t(key, params)`.
   * Only set by rules whose copy names something concrete (the offending
   * letters in a postal code); static messages leave it undefined.
   */
  params?: Record<string, string | number>;
} | null;

export function runValidate(
  rule: ValidateRule | undefined | null,
  raw: string,
  // Pattern is only consulted for the 'regex' rule; the originator authors it
  // on the field. Ignored by every other rule.
  pattern?: string | null,
): ValidationResult {
  if (!rule) return null;
  const value = (raw ?? '').trim();
  if (value === '') return null;
  switch (rule) {
    case 'sin':
      return validateSin(value);
    case 'email':
      return validateEmail(value);
    case 'phone':
      return validatePhone(value);
    case 'postal':
      return validatePostal(value);
    case 'regex':
      return validateRegex(value, pattern);
    default:
      return null;
  }
}

/**
 * Originator-authored regex rule. The value must match the whole authored
 * pattern. A pattern that fails to compile (should never happen — the builder +
 * BE both reject invalid patterns) fails OPEN: we treat the value as valid
 * rather than block the advisor on a config error. The generic errorKey is a
 * fallback; the renderer prefers the field's authored bilingual message when set.
 */
function validateRegex(value: string, pattern: string | null | undefined): ValidationResult {
  const source = (pattern ?? '').trim();
  if (source === '') return null;
  let re: RegExp;
  try {
    re = new RegExp(source);
  } catch {
    return null;
  }
  return re.test(value) ? null : { errorKey: 'formRenderer.validation.regex.format' };
}

/**
 * Canadian SIN — 9 digits, Luhn checksum, first digit 0–7 or 9.
 *   - 0 is ACCEPTED (QA RIF TC-07-04, the legacy forms' rule: the checksum alone decides).
 *   - 8 only appears on locally-issued cards in specific provinces; for the
 *     general account-opening flow we treat 8 as invalid to flag typos.
 * Strips spaces and hyphens before checking — copy/paste from a SIN card
 * (XXX-XXX-XXX) is forgiving.
 */
function validateSin(value: string): ValidationResult {
  const digits = value.replace(/[\s-]/g, '');
  if (!/^[0-9]{9}$/.test(digits)) return { errorKey: 'formRenderer.validation.sin.length' };
  // `000 000 000` is accepted (it passes Luhn: the sum is 0) — QA uses it to walk past this step.
  const first = digits.charCodeAt(0) - 48;
  if (first === 8) return { errorKey: 'formRenderer.validation.sin.firstDigit' };
  if (!luhnPasses(digits)) return { errorKey: 'formRenderer.validation.sin.checksum' };
  return null;
}

/**
 * Mod-10 Luhn checksum. Standard algorithm: walk right-to-left, double every
 * second digit, sum-of-digits when the doubled value > 9, total must be a
 * multiple of 10.
 */
function luhnPasses(digits: string): boolean {
  let sum = 0;
  let doubleIt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (doubleIt) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    doubleIt = !doubleIt;
  }
  return sum % 10 === 0;
}

/**
 * Local part — RFC 5322 §3.2.3 dot-atom: one or more `atext` atoms (letters,
 * digits and ``!#$%&'*+-/=?^_`{|}~``) joined by single dots. Writing it as
 * atom-dot-atom rather than one character class is what makes `.jane`, `jane.`
 * and `jane..doe` fail without a separate check.
 *
 * The one part of §3.2.3 left out is the quoted form (`"jane doe"@bmo.com`),
 * which would let whitespace and `@` into a value the rest of the pipeline —
 * trim-on-blur, the ZeroBounce batch, OneSpan's signer roster — treats as a
 * bare token. Unquoted addresses are what advisors type.
 */
const ATEXT = "[A-Za-z0-9!#$%&'*+\\-/=?^_`{|}~]";
const EMAIL_LOCAL = new RegExp(`^${ATEXT}+(?:\\.${ATEXT}+)*$`);

/**
 * One DNS label: letters/digits/hyphen, never starting or ending with a
 * hyphen. Length is checked by the caller (63 per label, RFC 1035).
 */
const DNS_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;

/**
 * The extension (right-most label). ICANN only delegates TLDs that are either
 * two-or-more ASCII LETTERS (`.com`, `.ca`, `.insurance`) or an IDN A-label —
 * punycode, always `xn--` + alphanumerics (`.xn--p1ai` = `.рф`). Nothing else
 * is reachable: no digits (`.c0m`), no hyphens, no punctuation, and no
 * single-letter TLD has ever been delegated.
 */
const EMAIL_TLD = /^(?:[A-Za-z]{2,}|xn--[A-Za-z0-9-]{2,})$/;

/**
 * Email — RFC 5322 dot-atom on the left of the `@`, deliverable DNS on the
 * right.
 *
 * The old check was shape-only (`[^\s@]+@[^\s@]+\.[^\s@]{2,}`): anything
 * without a space or a second `@` passed, so `jane@bmo.c#m`, `jane@-bmo.com`
 * and `jane@bmo.c0m` were all accepted and only failed later at ZeroBounce —
 * or, for a non-signatory field, never at all.
 *
 * The two halves are held to different standards ON PURPOSE. The local part is
 * opaque to everyone but the receiving mail server, so the spec is the only
 * authority available and we take it as written — `jane#doe@bmo.com` is a real
 * address, however odd it looks. The domain is not opaque: it has to resolve,
 * so it answers to DNS and to ICANN's TLD rules, which are narrower than the
 * grammar.
 *
 * Deliberately NOT checked here: whether the TLD is one ICANN has actually
 * delegated. That list changes (~1500 entries and growing) and shipping a
 * stale copy in the bundle would reject a real address; deliverability is
 * ZeroBounce's job on the eSignature step.
 */
function validateEmail(value: string): ValidationResult {
  const fail: ValidationResult = { errorKey: 'formRenderer.validation.email.format' };

  // Split on the LAST `@` so a second one lands in the local part and is
  // rejected there, rather than being read as part of the domain.
  const at = value.lastIndexOf('@');
  if (at < 1 || at === value.length - 1) return fail;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);

  // RFC 5321 §4.5.3.1 size limits: 64 octets local, 255 for the domain (253
  // once the implicit root dot and length byte are taken off).
  if (local.length > 64 || !EMAIL_LOCAL.test(local)) return fail;
  if (domain.length > 253) return fail;

  const labels = domain.split('.');
  // At least `domain` + `tld` — a bare `jane@localhost` is not deliverable
  // from a public form.
  if (labels.length < 2) return fail;
  if (!labels.every((label) => label.length <= 63 && DNS_LABEL.test(label))) return fail;
  if (!EMAIL_TLD.test(labels[labels.length - 1] ?? '')) return fail;

  return null;
}

/**
 * Phone — exactly 10 digits after stripping common separators (space, dash,
 * parens, dot). North-American format; no country code expected.
 */
function validatePhone(value: string): ValidationResult {
  const digits = value.replace(/[\s\-().]/g, '');
  if (!/^[0-9]{10}$/.test(digits)) {
    return { errorKey: 'formRenderer.validation.phone.length' };
  }
  return null;
}

/** Leading letter: A-Z minus D F I O Q U W Z (canada-post specific). */
const POSTAL_FIRST_LETTERS = 'ABCEGHJKLMNPRSTVXY';
/** Letters 2 + 3: A-Z minus D F I O Q U (W and Z are allowed mid-code). */
const POSTAL_INNER_LETTERS = 'ABCEGHJKLMNPRSTVWXYZ';
/** Shape only — letter/digit alternation, any letter in the letter slots. */
const POSTAL_SHAPE = /^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$/;

/**
 * Canadian postal code — A1A 1A1 pattern, case-insensitive, optional middle
 * space. Canada Post forbids D / F / I / O / Q / U in every letter position,
 * plus W and Z in the leading position. We enforce the same restricted
 * alphabet in all three letter slots so an invalid `M5D 3F8` doesn't pass;
 * downstream services still do existence checks for assignments that aren't
 * currently in use.
 *
 * The two failure modes get their own message. A value that isn't even
 * letter/digit alternation ("M5V 3A" / "12345") is a shape problem, so the
 * advisor needs the A1A 1A1 example. A value that IS shaped right but uses a
 * forbidden letter ("M5D 3F8") would be baffling next to that example — it
 * already looks like A1A 1A1 — so it gets a message naming the letters at
 * fault instead.
 */
function validatePostal(value: string): ValidationResult {
  const compact = value.replace(/\s/g, '').toUpperCase();
  if (!POSTAL_SHAPE.test(compact)) {
    return { errorKey: 'formRenderer.validation.postal.format' };
  }
  const offenders = [compact.charAt(0), compact.charAt(2), compact.charAt(4)].filter(
    (letter, index) =>
      !(index === 0 ? POSTAL_FIRST_LETTERS : POSTAL_INNER_LETTERS).includes(letter),
  );
  if (offenders.length > 0) {
    // De-duplicated so "D1D 1A1" reads "the letter D", not "D, D".
    const letters = [...new Set(offenders)];
    return {
      errorKey: 'formRenderer.validation.postal.invalidLetter',
      params: { letters: letters.join(', '), count: letters.length },
    };
  }
  return null;
}

// ---- Input formatting masks. Run on every keystroke. Each mask strips its
// own separators from the input first, caps to the rule's max-digit/char
// count, then re-inserts separators at fixed positions. The result is what
// the advisor sees AND what we store — runValidate strips the separators
// again before the semantic check, so storage/validation stay aligned.

export type MaskedRule = 'sin' | 'phone' | 'postal';

/** True if the rule has a paired format mask. SIN / phone / postal do; email
 *  doesn't (no canonical fixed shape). */
export function isMaskedRule(rule: string | null | undefined): rule is MaskedRule {
  return rule === 'sin' || rule === 'phone' || rule === 'postal';
}

/**
 * Format a ShortText input value according to its `validate` rule. Returns
 * the input untouched when the rule has no mask. The caller is expected to
 * skip `applyInputRestriction` when a mask is in play — the mask already
 * filters the character set.
 */
export function applyFormatMask(rule: string | null | undefined, raw: string): string {
  if (!isMaskedRule(rule)) return raw;
  if (rule === 'sin') return maskSin(raw);
  if (rule === 'phone') return maskPhoneInput(raw);
  return maskPostal(raw);
}

/** "123 456 789" — three groups of three digits separated by single spaces. */
function maskSin(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

/**
 * The typed / pasted path into `maskPhone`. Identical grouping, plus a country-code strip that
 * only live input wants.
 *
 * "+1 416 555 0211" (or "1-416-555-0211") used to mask to "(141) 655-5021": the mask kept the
 * country code, shifted the real number one slot right and dropped its last digit. The result is
 * still TEN digits, so `validatePhone` passed it and nothing was ever shown to the advisor — the
 * eSign SMS went to whoever owns the wrong number (TFSA v38).
 *
 * The rule is **keep the last ten digits** — a NANP number is the TAIL of whatever was typed or
 * pasted, since everything in front of it is a country code or a dial-out prefix. That fixes the
 * country-code case without a special branch, and it salvages "+44 20 7946 0958" or a 15-digit
 * E.164 string into the only ten digits this field can represent.
 *
 * A ten-digit value is kept whatever it starts with, so placeholder numbers like "(111) 111-1111"
 * are typeable. The earlier rule ate any leading `1` on the NANP argument that area and exchange
 * codes both start 2-9 — true of real numbers, but it made those untypeable, and it left a
 * country-coded number pasted one digit short ("+1 416 555 021", ten digits) reading as
 * "(141) 655-5021". Ten digits beginning with `1` cannot be both enterable and treated as
 * country-coded; this picks enterable.
 *
 * The `startsWith` branch is what keeps an OVERFLOW KEYSTROKE swallowed rather than sliding the
 * window. The mask re-runs over its own previous output, so a digit typed onto the end of a
 * complete number arrives as "(416) 555-0211" + "9" — the already-masked ten digits, with the
 * extra appended. A paste never looks like that ("+1 416 555 0211" does not begin with the mask
 * of its own first ten digits), which is what lets one function serve both: recognise the mask's
 * own output at the front and the eleventh character is a stray keystroke to drop, otherwise the
 * leading digits are a prefix to drop. Without it, typing one digit too many would silently shift
 * a complete number left and lose its first digit.
 *
 * What that branch costs, so nobody "fixes" it later: a country code typed in DIGIT BY DIGIT is
 * indistinguishable from an overflow keystroke — same previous value, same raw text — so it no
 * longer resolves. "1" then "416555021" dead-ends at "(141) 655-5021" and the advisor has to
 * clear it. Visibly wrong, not silently wrong, and PASTING the same number still lands correctly,
 * which is the path TFSA v38 was reported on. The two behaviours are mutually exclusive; this
 * picks the swallow.
 *
 * `displayPhone` deliberately does NOT route through here — it formats values this mask never
 * saw (an advisor profile's "416-970-8445"), so it keeps its conservative 11-digits-only strip.
 */
function maskPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length > 10) {
    const head = maskPhone(digits.slice(0, 10));
    if (raw.startsWith(head)) return head;
  }
  return maskPhone(digits.slice(-10));
}

/** "(416) 555-1234" — North-American 10-digit phone with parens / dash. The
 *  leading `(` lands as soon as the advisor types the first digit so the
 *  intent of the field is obvious. */
function maskPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** "M5V 3A8" — 6 alphanumeric characters with a single space after the
 *  third. Strips anything that isn't a letter or digit (pasted hyphens /
 *  punctuation no longer leak into letter slots) and auto-uppercases so the
 *  advisor doesn't have to think about case. */
function maskPostal(raw: string): string {
  // Letter, digit, letter, digit, letter, digit (A1A 1A1): a character that does not fit the next
  // slot is dropped, as the legacy form's S0S 0S0 mask did — '12345A' used to become '123 45A'
  // (QA Reg/Non-Reg D-27).
  let c = '';
  for (const ch of raw.toUpperCase()) {
    if (c.length === 6) break;
    const wantLetter = c.length % 2 === 0;
    if (wantLetter ? /[A-Z]/.test(ch) : /[0-9]/.test(ch)) c += ch;
  }
  if (c.length <= 3) return c;
  return `${c.slice(0, 3)} ${c.slice(3)}`;
}

/**
 * Whether a Date field's value satisfies its `minAge` rule.
 *
 * The renderer already greys out-of-range days via the picker's
 * `disabledDate`, but AntD's DatePicker deliberately accepts TYPED input
 * (see the `dateFormat` comment in FieldRenderer) — so the calendar alone is
 * cosmetic. Typing a future date, or a date under the minimum age, was
 * accepted and saved. This is the enforcement half.
 *
 * `minAge: 0` means "no future dates" (the cutoff is today), which is how the
 * FCCP "Date of Formation" rule is expressed without a second property.
 *
 * Empty values short-circuit — "required" is enforced separately, so a blank
 * optional date must not surface an age error.
 *
 * @param raw   ISO `YYYY-MM-DD` as stored by the Date renderer.
 * @param today Injectable for tests; defaults to the current date.
 */
export function validateMinAge(
  minAge: number | null | undefined,
  raw: string | null | undefined,
  today: Date = new Date(),
): ValidationResult {
  if (minAge === null || minAge === undefined) return null;
  const entered = parseIsoDate(raw);
  if (!entered) return null; // Blank or unparseable — the picker owns format errors.

  // Cutoff = today minus minAge years. Compared date-only, so a birthday
  // falling exactly on the cutoff passes (you ARE 18 that day).
  const cutoff = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());

  if (entered.getTime() > cutoff.getTime()) {
    return minAge === 0
      ? { errorKey: 'formRenderer.validation.date.noFuture' }
      : { errorKey: 'formRenderer.validation.date.minAge', params: { years: minAge } };
  }
  return null;
}

/** Parses a stored ISO `YYYY-MM-DD` into a local date, or null when it isn't one. */
function parseIsoDate(raw: string | null | undefined): Date | null {
  const value = (raw ?? '').trim();
  if (value === '') return null;
  const parts = value.split('-').map(Number);
  const [y, m, d] = parts;
  if (parts.length !== 3 || !y || !m || !d || parts.some((n) => !Number.isFinite(n))) {
    return null;
  }
  return new Date(y, m - 1, d);
}

/** The authored Date-field rules — a structural slice of `WorkflowField`. */
export type DateRules = {
  minAge?: number | null;
  /** The oldest allowed age in whole years: a day more than this many years ago is refused. */
  maxAge?: number | null;
  /** With `mustFollowFieldId`: the same day is allowed (on or after, not strictly after). */
  mustFollowAllowSame?: boolean | null;
  /** Refuse any day before today (today itself is allowed). */
  noPastDates?: boolean | null;
  /** Id of another Date field this value must be strictly after. */
  mustFollowFieldId?: string | null;
};

/** What the rules need from outside the field itself. */
export type DateRuleContext = {
  /** The `mustFollowFieldId` field's current value, already resolved for this repeatable
   *  instance (see `scopeValuesToInstance`). Blank or non-ISO skips the rule. */
  mustFollowValue?: unknown;
  /** That field's label in the current language — interpolated as `{{field}}`. */
  mustFollowLabel?: string;
  /** Injectable for tests; defaults to the current date. */
  today?: Date;
};

/**
 * Every authored rule on a Date field, checked against one ISO value — the single function the
 * blur message, the picker's disabled days and the Next gate all call, so the three cannot
 * disagree. Rules run in a fixed order and the first failure wins:
 *
 *  1. `minAge` (and its `0` = no-future form);
 *  2. `noPastDates` — a day before today (QA DP-009 / DP-021: a past start date was accepted);
 *  3. `mustFollowFieldId` — on or before the referenced date (QA DP-010: an end date equal to
 *     the start date was accepted). Skipped while the referenced date is blank: there is
 *     nothing yet to be after.
 *
 * Blank values return null — `required` owns an empty field.
 */
export function validateDateRules(
  rules: DateRules,
  raw: string | null | undefined,
  context: DateRuleContext = {},
): ValidationResult {
  const today = context.today ?? new Date();
  const age = validateMinAge(rules.minAge, raw, today);
  if (age) return age;
  const entered = parseIsoDate(raw);
  if (!entered) return null;

  // The oldest allowed age (QA Reg/Non-Reg D-16: an investor must be 18–80). The boundary day
  // itself — exactly `maxAge` years ago — is allowed, like min-age's.
  if (rules.maxAge != null) {
    const oldest = new Date(today.getFullYear() - rules.maxAge, today.getMonth(), today.getDate());
    if (entered.getTime() < oldest.getTime()) {
      return { errorKey: 'formRenderer.validation.date.maxAge', params: { years: rules.maxAge } };
    }
  }

  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (rules.noPastDates && entered.getTime() < startOfToday.getTime()) {
    return { errorKey: 'formRenderer.validation.date.noPast' };
  }

  if (rules.mustFollowFieldId) {
    const reference =
      typeof context.mustFollowValue === 'string' ? parseIsoDate(context.mustFollowValue) : null;
    const tooEarly =
      reference !== null &&
      (rules.mustFollowAllowSame
        ? entered.getTime() < reference.getTime()
        : entered.getTime() <= reference.getTime());
    if (tooEarly) {
      return {
        errorKey: 'formRenderer.validation.date.mustFollow',
        params: { field: context.mustFollowLabel ?? '' },
      };
    }
  }
  return null;
}

/**
 * The DEFAULT format the Date control displays and accepts typed input in — what every workflow
 * without its own `dateFormat` uses. A workflow may choose `MM/DD/YYYY` instead (QA D-01); the
 * runtime reads the effective one from `DateFormatContext`. Shared by the picker and
 * {@link validateTypedDate} so the two can never drift apart.
 */
export const DATE_INPUT_FORMAT: WorkflowDateFormat = 'DD/MM/YYYY';

/**
 * Checks what the advisor TYPED into a Date control, at blur.
 *
 * AntD's DatePicker silently throws away typed text it can't use — an impossible date
 * (`13/45/1990`) or one its `disabledDate` greys out (a future birth date) — and leaves the field
 * blank with no word of explanation (QA D-35). The typed text is still on the input when blur
 * fires, so this names the problem instead: an invalid date, or whichever of the field's own
 * rules ({@link validateDateRules}) it breaks.
 *
 * Blank text returns null — `required` owns an empty field.
 */
export function validateTypedDate(
  text: string | null | undefined,
  rules: DateRules,
  context: DateRuleContext = {},
  /** The format the text was typed in — the workflow's, so `03/04/2020` is 3 April under
   *  DD/MM/YYYY and 4 March under MM/DD/YYYY. */
  format: WorkflowDateFormat = DATE_INPUT_FORMAT,
): ValidationResult {
  if ((text ?? '').trim() === '') return null;
  const iso = parseTypedDate(text, format);
  if (!iso) return { errorKey: 'formRenderer.validation.date.invalid' };
  return validateDateRules(rules, iso, context);
}

/**
 * The ISO date a Date control's typed text names, or null when it is blank or not a real date
 * in `format`. What the blur handlers COMMIT when the picker has dropped a typed date its
 * `disabledDate` refuses: left blank, an optional date (the DCA end date, QA DP-010) let Next
 * through with the rule's message still showing beneath it. Kept, the date rules hold Next.
 */
export function parseTypedDate(
  text: string | null | undefined,
  format: WorkflowDateFormat = DATE_INPUT_FORMAT,
): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((text ?? '').trim());
  if (!match) return null;
  const [first, second, y] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const [d, m] = format === 'MM/DD/YYYY' ? [second, first] : [first, second];
  const date = new Date(y, m - 1, d);
  // Round-trip catches 31/02 and 13/45, which Date silently rolls over.
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * True when a CheckboxGroup's current selection satisfies its authored
 * `minSelections` floor.
 *
 * `required` on a CheckboxGroup only ever meant "non-empty", so an
 * acknowledgement gate — RESP's three eligibility cards, where the source
 * form disables Next until every card is ticked — could not be expressed:
 * ticking one card let the advisor continue. This is the cardinality half.
 *
 * Note the polarity difference from `blockingValues`, which fires when a
 * value IS selected and can never fire on an empty selection. That rule
 * stops a workflow on a disqualifying answer; this one holds it until
 * enough boxes are ticked.
 *
 * Fields without a floor, and non-array values, pass — the caller's
 * `required` / `isFilled` check owns those cases.
 */
export function meetsMinSelections(
  minSelections: number | null | undefined,
  value: unknown,
): boolean {
  if (typeof minSelections !== 'number' || !Number.isFinite(minSelections)) return true;
  if (minSelections < 1) return true;
  if (!Array.isArray(value)) return false;
  return value.length >= minSelections;
}

/**
 * A North-American number in the shape the form's own phone fields type it — "(416) 970-8445" —
 * whatever its source stored (the advisor's profile phone arrives as "416-970-8445"; TFSA QA
 * TF-15). Anything that is not 10 digits (or 11 with the leading 1) is shown untouched.
 * Display only (eSignature Summary, Application Review): the stored value is unchanged.
 */
export function displayPhone(phone: string | null | undefined): string | null {
  if (!phone) return phone ?? null;
  const digits = phone.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return ten.length === 10 ? maskPhone(ten) : phone;
}
